import { PLANBASE_CONFIG } from '../config';
import { type ComponentType, type SVGProps } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import SidebarProfilePopover from './nav/SidebarProfilePopover';

import { BookmarkIcon, FolderIcon, MapIcon, SparklesIcon, UserIcon } from '@heroicons/react/24/outline';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

const isNative = Capacitor.isNativePlatform();

interface NavigationItem {
  name: string;
  href: string;
  icon: IconComponent;
}

const navigation: NavigationItem[] = [
  { name: 'Discover', href: '/discover', icon: MapIcon },
  { name: 'Portfolio', href: '/portfolio', icon: FolderIcon },
  { name: 'Orbi AI', href: '/orbi', icon: SparklesIcon },
  { name: 'Collection', href: '/collections', icon: BookmarkIcon },
];

export default function Sidebar() {
  const location = useLocation();
  return (
    <div className="fixed bottom-0 left-0 right-0 md:sticky md:top-0 md:h-screen md:inset-y-0 md:left-auto md:right-auto w-full md:w-64 md:bg-background md:border-t-0 flex md:flex-col z-50 md:flex-shrink-0 bg-background/60 backdrop-blur-xl backdrop-saturate-150 border-t border-white/10 dark:border-white/5" style={isNative ? { paddingBottom: 'env(safe-area-inset-bottom)' } : {}}>

      {/* Logo — desktop only */}
      <div className="hidden md:flex p-4 px-4 items-center justify-start">
        {PLANBASE_CONFIG.project.logo_url ? (
          <div className="flex items-center gap-2">
            <img src={PLANBASE_CONFIG.project.logo_url} alt="Planbase" className="h-7 w-7 rounded-full object-cover flex-shrink-0" />
            <span className="text-sm font-medium text-foreground">Planbase</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <MapIcon className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-medium text-foreground">Planbase</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex md:flex-col flex-1 px-2 py-2 md:py-4 md:space-y-0.5 justify-around md:justify-start">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const ItemIcon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex flex-col md:flex-row items-center justify-center md:justify-start py-2 px-2 md:px-3 rounded-lg transition-all gap-0 md:gap-2.5 ${
                isActive
                  ? 'text-primary border border-transparent md:bg-muted md:border-border/40'
                  : 'text-muted-foreground hover:text-foreground border border-transparent hover:bg-white hover:border-border/40 dark:hover:bg-muted dark:hover:border-transparent'
              }`}
            >
              <ItemIcon className="h-5 w-5 mb-0.5 md:mb-0 flex-shrink-0" />
              <span className={`text-[10px] md:text-xs ${isActive ? 'font-medium' : 'font-normal'}`}>
                {item.name}
              </span>
            </Link>
          );
        })}

        {/* Mobile: profile link */}
        <Link
          to="/profile"
          className={`md:hidden flex flex-col items-center justify-center py-2 px-2 rounded-lg transition-all border border-transparent ${
            location.pathname === '/profile' || location.pathname.startsWith('/profile/')
              ? 'text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-white hover:border-border/40'
          }`}
        >
          <UserIcon className="h-5 w-5 mb-0.5" />
          <span className="text-[10px]">Profile</span>
        </Link>
      </nav>

      {/* Footer — desktop only. Shared popover component; behaviour and
          admin gating live in one place now (see SidebarProfilePopover). */}
      <div className="hidden md:block px-2 pb-2 md:px-3 md:pb-3">
        <SidebarProfilePopover />
      </div>
    </div>
  );
}
